// ============================================
// MoltiVerse v3 - Mission Control Stats
// ============================================
// Real-time stats that look sick as hell

import { useEffect, useState, useMemo } from 'react';
import { useUniverseStore } from '@/stores/universe-store';
import { SUBMOLT_COLORS } from '@/lib/types';
// import { AnalyticsPanel } from './AnalyticsPanel'; // Disabled for now

export function StatsOverlay() {
  // const [showAnalytics, setShowAnalytics] = useState(false); // Disabled for now
  const { stats, graphData, isLoading, view, settings, toggleShowAllAgents } = useUniverseStore();
  const [time, setTime] = useState(new Date());
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // Calculate real-time insights from the graph data - BULLETPROOF
  const insights = useMemo(() => {
    // Safe array access - handle undefined/null graphData
    const nodes = Array.isArray(graphData?.nodes) ? graphData.nodes : [];
    if (nodes.length === 0) return null;
    
    try {
      // Total karma in the network (with safe number coercion)
      const totalKarma = nodes.reduce((sum, n) => sum + (Number(n?.karma) || 0), 0);
      const avgKarma = Math.round(totalKarma / nodes.length);
      
      // Top agents by karma (with null-safe sorting)
      const topByKarma = [...nodes]
        .filter(n => n && typeof n.karma === 'number')
        .sort((a, b) => (b.karma || 0) - (a.karma || 0))
        .slice(0, 3);
      
      // Most connected (by connection_count)
      const topByConnections = [...nodes]
        .filter(n => n && typeof n.connection_count === 'number')
        .sort((a, b) => (b.connection_count || 0) - (a.connection_count || 0))
        .slice(0, 3);
      
      // Submolt distribution (with null-safe key access)
      const submoltCounts = new Map<string, number>();
      nodes.forEach(n => {
        if (n?.main_submolt) {
          const count = submoltCounts.get(n.main_submolt) || 0;
          submoltCounts.set(n.main_submolt, count + 1);
        }
      });
      const topSubmolts = Array.from(submoltCounts.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3);
      
      // Activity level (based on post count)
      const totalPosts = nodes.reduce((sum, n) => sum + (Number(n?.post_count) || 0), 0);
      
      return {
        totalKarma,
        avgKarma,
        topByKarma,
        topByConnections,
        topSubmolts,
        totalPosts,
      };
    } catch (err) {
      console.error('[StatsOverlay] Error computing insights:', err);
      return null;
    }
  }, [graphData?.nodes]);

  // Update clock every second
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit' 
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: '2-digit', 
      year: 'numeric' 
    }).toUpperCase();
  };

  // Hide in pathfinder mode to avoid overlap with agent panel
  if (view.mode === 'pathfinder') {
    return null;
  }

  return (
    <>
    <div className="fixed top-3 sm:top-6 left-3 sm:left-6 z-40">
      {/* Toggle button - always visible */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="pointer-events-auto mb-2 flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-1.5 bg-black/60 backdrop-blur-md border border-white/10 rounded-lg hover:border-cyber-glow/30 hover:bg-black/80 transition-all duration-200 group"
      >
        <svg 
          className={`w-4 h-4 text-cyber-glow transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isCollapsed ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
        </svg>
        <span className="font-mono text-[10px] text-white/60 group-hover:text-white/80 tracking-wider">
          {isCollapsed ? 'SHOW STATS' : 'HIDE'}
        </span>
      </button>

      {/* Main stats panel - collapsible */}
      <div 
        className={`transition-all duration-300 ease-out pointer-events-none ${
          isCollapsed ? 'opacity-0 scale-95 h-0 overflow-hidden' : 'opacity-100 scale-100'
        }`}
      >
        <div className="relative">
          {/* Corner decoration */}
          <div className="absolute -top-1 -left-1 w-3 h-3 border-l-2 border-t-2 border-cyber-glow/50" />
          <div className="absolute -bottom-1 -right-1 w-3 h-3 border-r-2 border-b-2 border-cyber-glow/50" />
          
          <div className="bg-black/60 backdrop-blur-md border border-white/10 rounded-lg p-4 min-w-[280px]">
          {/* Header with time */}
          <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className="w-2 h-2 rounded-full bg-cyber-glow" />
                <div className="absolute inset-0 w-2 h-2 rounded-full bg-cyber-glow animate-ping" />
              </div>
              <span className="font-mono text-[10px] text-cyber-glow tracking-widest">LIVE</span>
            </div>
            <div className="text-right">
              <div className="font-mono text-lg text-white font-bold tracking-wider glow-green-subtle">
                {formatTime(time)}
              </div>
              <div className="font-mono text-[9px] text-white/40 tracking-wider">
                {formatDate(time)}
              </div>
            </div>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3">
            <StatCard 
              label="AGENTS" 
              value={stats.totalAgents || graphData.nodes.length}
              icon="◉"
              color="#00ff88"
              pulse={isLoading}
            />
            <StatCard 
              label="BONDS" 
              value={stats.totalInteractions || graphData.links.length}
              icon="◈"
              color="#0088ff"
            />
            <StatCard 
              label="POSTS" 
              value={stats.totalPosts}
              icon="◆"
              color="#8000ff"
            />
            <StatCard 
              label="SIGNALS" 
              value={stats.totalInteractions}
              icon="◇"
              color="#ffaa00"
            />
          </div>
          
          {/* Sampling indicator & show all toggle */}
          {stats.totalAgents > graphData.nodes.length && graphData.nodes.length > 0 && !settings.showAllAgents && (
            <div className="mt-3 px-2 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[9px] text-amber-400">
                  Showing top {graphData.nodes.length.toLocaleString()} of {stats.totalAgents.toLocaleString()}
                </span>
                <button
                  onClick={toggleShowAllAgents}
                  disabled={isLoading}
                  className="pointer-events-auto font-mono text-[8px] px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 hover:bg-amber-500/30 transition-colors disabled:opacity-50"
                >
                  Show All
                </button>
              </div>
            </div>
          )}
          
          {/* Experimental mode indicator */}
          {settings.showAllAgents && (
            <div className="mt-3 px-2 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <span className="font-mono text-[9px] text-purple-400 block">
                    ⚠️ EXPERIMENTAL: All {graphData.nodes.length.toLocaleString()} agents
                  </span>
                  <span className="font-mono text-[8px] text-purple-400/60">
                    May be laggy on slower devices
                  </span>
                </div>
                <button
                  onClick={toggleShowAllAgents}
                  disabled={isLoading}
                  className="pointer-events-auto font-mono text-[8px] px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 transition-colors disabled:opacity-50"
                >
                  Default
                </button>
              </div>
            </div>
          )}

          {/* Total Karma Bar */}
          {insights && (
            <div className="mt-4 pt-3 border-t border-white/10">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[9px] text-white/40 tracking-wider">TOTAL NETWORK KARMA</span>
                <span className="font-mono text-sm font-bold text-amber-400">
                  {insights.totalKarma.toLocaleString()}
                </span>
              </div>
              <div className="flex items-center justify-between text-[10px]">
                <span className="text-white/30">Avg per agent:</span>
                <span className="text-amber-400/80">{insights.avgKarma.toLocaleString()}</span>
              </div>
            </div>
          )}
          
          {/* Top Agents Mini Leaderboard */}
          {insights && insights.topByKarma.length > 0 && (
            <div className="mt-4 pt-3 border-t border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-amber-400">👑</span>
                <span className="font-mono text-[9px] text-white/40 tracking-wider">TOP KARMA HOLDERS</span>
              </div>
              <div className="space-y-1.5">
                {insights.topByKarma.map((agent, idx) => (
                  <div key={agent.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-white/30 w-4">#{idx + 1}</span>
                      <div 
                        className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold"
                        style={{ 
                          backgroundColor: `${SUBMOLT_COLORS[agent.main_submolt]}30`,
                          color: SUBMOLT_COLORS[agent.main_submolt],
                        }}
                      >
                        {agent.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="font-mono text-[11px] text-white/80 truncate max-w-[80px]">
                        {agent.name}
                      </span>
                    </div>
                    <span className="font-mono text-[11px] text-amber-400 font-bold">
                      {(agent.karma || 0).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Top Submolts */}
          {insights && insights.topSubmolts.length > 0 && (
            <div className="mt-4 pt-3 border-t border-white/10">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-purple-400">🏛️</span>
                <span className="font-mono text-[9px] text-white/40 tracking-wider">TOP FACTIONS</span>
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {insights.topSubmolts.map(([submolt, count]) => (
                  <div 
                    key={submolt}
                    className="px-2 py-1 rounded-full text-[10px] font-mono"
                    style={{ 
                      backgroundColor: `${SUBMOLT_COLORS[submolt]}20`,
                      color: SUBMOLT_COLORS[submolt],
                      border: `1px solid ${SUBMOLT_COLORS[submolt]}40`,
                    }}
                  >
                    m/{submolt} <span className="opacity-60">({count})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Streaming indicator */}
        {isLoading && (
          <div className="mt-2 flex items-center gap-2 px-3 py-1.5 bg-black/40 border border-cyber-glow/20 rounded">
            <div className="flex gap-0.5">
              {[0, 1, 2].map(i => (
                <div 
                  key={i}
                  className="w-1 h-3 bg-cyber-glow rounded-full"
                  style={{
                    animation: 'pulse 1s ease-in-out infinite',
                    animationDelay: `${i * 0.15}s`,
                  }}
                />
              ))}
            </div>
            <span className="font-mono text-[10px] text-cyber-glow/80">STREAMING DATA...</span>
          </div>
        )}
        </div>

        {/* System status indicators */}
        <div className="mt-4 flex gap-3">
          <StatusPill label="ORACLE" status="online" />
          <StatusPill label="TIMELINE" status="ready" />
          <StatusPill label="NEURAL NET" status="active" />
        </div>
        
        {/* Analytics Button - Disabled for now */}
        {/* <button
          onClick={() => setShowAnalytics(true)}
          className="pointer-events-auto mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-500/30 rounded-lg hover:border-purple-500/50 hover:from-purple-500/30 hover:to-cyan-500/30 transition-all duration-200 group"
        >
          <svg className="w-4 h-4 text-purple-400 group-hover:text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span className="font-mono text-[10px] text-white/70 tracking-wider group-hover:text-white/90">
            VIEW PROGRESSION
          </span>
        </button> */}
      </div>
    </div>
    </>
  );
}

function StatCard({ 
  label, 
  value, 
  total,
  icon,
  color,
  pulse = false,
}: { 
  label: string; 
  value: number; 
  total?: number;
  icon: string;
  color: string;
  pulse?: boolean;
}) {
  const [displayValue, setDisplayValue] = useState(0);

  // Animate number counting up
  useEffect(() => {
    if (value === displayValue) return;
    
    const duration = 500;
    const steps = 20;
    const increment = (value - displayValue) / steps;
    let current = displayValue;
    
    const timer = setInterval(() => {
      current += increment;
      if ((increment > 0 && current >= value) || (increment < 0 && current <= value)) {
        setDisplayValue(value);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.round(current));
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <div className="relative group">
      {/* Background glow on hover */}
      <div 
        className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"
        style={{ background: `${color}20` }}
      />
      
      <div 
        className="relative p-3 rounded-lg border transition-all duration-300"
        style={{ 
          borderColor: `${color}20`,
          background: `linear-gradient(135deg, ${color}08 0%, transparent 100%)`,
        }}
      >
        <div className="flex items-center justify-between mb-1">
          <span className="font-mono text-[9px] text-white/40 tracking-wider">{label}</span>
          <span style={{ color }} className="text-sm opacity-60">{icon}</span>
        </div>
        <div className="flex items-baseline gap-1">
          <span 
            className="font-display text-2xl font-bold stat-value"
            style={{ color }}
          >
            {displayValue.toLocaleString()}
          </span>
          {pulse && (
            <span 
              className="w-1.5 h-1.5 rounded-full animate-pulse"
              style={{ background: color }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function StatusPill({ label, status }: { label: string; status: 'online' | 'ready' | 'active' | 'offline' }) {
  const colors = {
    online: '#00ff88',
    ready: '#0088ff',
    active: '#ffaa00',
    offline: '#ff3366',
  };

  return (
    <div 
      className="flex items-center gap-1.5 px-2 py-1 rounded-full border bg-black/40"
      style={{ borderColor: `${colors[status]}30` }}
    >
      <div 
        className="w-1.5 h-1.5 rounded-full"
        style={{ 
          background: colors[status],
          boxShadow: `0 0 6px ${colors[status]}`,
        }}
      />
      <span className="font-mono text-[8px] text-white/50 tracking-wider">{label}</span>
    </div>
  );
}
